import React, { useState, useEffect, useRef, useCallback } from "react";
import { BrPageContext, BrComponentContext, BrManageContentButton } from "@bloomreach/react-sdk";
import SearchToolbar from "./SearchToolbar/SearchToolbar";
import SearchToolbarInfo from "./SearchToolbar/SearchToolbarInfo";
import Filter from "./Filter/Filter";
import { REFINEMENTS, SORT_OPTION, CONTENT_TYPE } from "../Global/GlobalConstants";
import "./SearchController.scss";
import SearchInputFilter from "./SearchInputFilter/SearchInputFilter";
import SearchRefinementsController from "./SearchRefinementController/SearchRefinementsController";
import { Desktop, Mobile, IsMobileContent, IsMobileNav } from "../../media-queries/MediaQueries";
import ErrorComponent from "../Common/ErrorComponent/ErrorComponent";
import SearchInput from "./SearchInput/SearchInput";
import NoResults from "../SiteSearchResults/NoResults";
import LoadingSpinner from "../Global/LoadingSpinner/LoadingSpinner";
import { DISPLAYMODE } from "./SearchToolbar/DisplayMode";
import { CONTENT_TYPES, FILTER_CONSTANTS, SEARCH_CONFIG, SORT_VALUES } from "./searchConstants";
import { getSearchContentType, positionModal, defaultDesktopToggleValues, totalSearchItemsCount } from "./searchUtils";
import { useSearchFilters } from "./hooks/useSearchFilters";
import { useSearchQuery } from "./hooks/useSearchQuery";
import SearchResultRenderer from "./SearchResultRenderer";
import SearchProviders from "./SearchProviders";
import { fetchRefinementsCached } from "../../utils/cachedFetch";

// Feature flags
const ENABLE_HOVER_MODE = true; // Set to true to enable grid compact hover mode

function SearchController({ isSingleTypeSearch }) {
  const page = React.useContext(BrPageContext);
  const component = React.useContext(BrComponentContext);

  const { NUMBER_TO_LOAD_MORE, NUM_OF_ROWS } = SEARCH_CONFIG;
  const { CATEGORY_FILTER } = FILTER_CONSTANTS;
  const { ALPHABETICAL: SORTALPHABETICAL, MOST_RECENT: SORTMOSTRECENT } = SORT_VALUES;
  const {
    PRODUCT: CONTENTTYPEPRODUCT,
    ARTICLE: CONTENTTYPEARTICLE,
    MEDIA: CONTENTTYPEMEDIA,
    WEBPAGE: CONTENTTYPEWEBPAGE,
  } = CONTENT_TYPES;

  const isMobile = IsMobileContent();
  const isMobileNav = IsMobileNav();

  let {
    document: documentRef,
    servletUrl, // curated listing only
    brsmSearchURL, // primary search url
    patentSearchServletURL, // patent only
    componentConfigurationPath,
    noResultsPopularProductCodes,
    productServletURL,
  } = component.getModels();

  let configurationPath;
  if (componentConfigurationPath) {
    configurationPath =
      componentConfigurationPath?.substring(0, componentConfigurationPath.lastIndexOf("/") + 1) +
      encodeURIComponent(
        componentConfigurationPath?.substring(componentConfigurationPath.lastIndexOf("/") + 1)
      );
  }
  const {
    hideSort,
    defaultSort = "featured",
    productHeight,
    productWidth,
  } = component.getParameters();

  // coming from location x, y, etc - configPath (home in BE, so BE knows it's doing search under specific section)

  const { CURATEDLISTING, SITESEARCHRESULTS, PATENTSEARCHLISTING } = CONTENT_TYPE;

  // Use custom hooks
  const searchFilters = useSearchFilters();
  const searchQuery = useSearchQuery();

  const [searchResults, setSearchResults] = useState(null);
  const [autoCorrectOverride, setAutoCorrectOverride] = useState(null);
  const [sortValue, setSortValue] = useState(searchQuery.urlParams.get("sort") || defaultSort);
  const [searchTerm, setSearchTerm] = useState(searchQuery.urlParams.get("q") || "");
  const [renderedSearchResults, setRenderedSearchResults] = useState(null);
  const [numberToShow, setNumberToShow] = useState(NUM_OF_ROWS);
  const [refinements, setRefinements] = useState({ counts: [], didYouMean: [] });
  const [activeRefinement, setActiveRefinement] = useState(searchQuery.urlParams.get("type") || null);
  const [gotRefinements, setGotRefinements] = useState(false);
  const [loadingResults, setLoadingResults] = useState(true);
  const [pageNum, setPageNum] = useState(parseInt(searchQuery.urlParams.get("page")) || 1);
  const [categoryFilter, setCategoryFilter] = useState("");
  const [displayMode, setDisplayMode] = useState(ENABLE_HOVER_MODE ? DISPLAYMODE.GRIDCOMPACT : DISPLAYMODE.GRIDEXPANDED);

  //need a special var of when to listen for search updated needed
  const [reRunSearch, setReRunSearch] = useState(true);

  const isFirstRender = useRef(true);
  const isFirstRefinementGetRender = useRef(true);
  const isFirstReRender = useRef(true);

  const modalContainerRef = useRef(null);

  // Set display mode to LIST for patent search
  useEffect(() => {
    const contentDocument = page.getContent(documentRef);
    if (contentDocument) {
      const { contentType = null } = contentDocument.getData();
      if (contentType === PATENTSEARCHLISTING) {
        setDisplayMode(DISPLAYMODE.LIST);
      }
    }
  }, [page, documentRef, PATENTSEARCHLISTING]);

  // Set display mode to LIST for documents, articles, media, and webpages
  useEffect(() => {
    const contentDocument = page.getContent(documentRef);
    if (contentDocument) {
      const { contentType = null } = contentDocument.getData();
      const searchContentType = getSearchContentType(contentType);
      
      // For site search results with active refinement
      if (contentType === SITESEARCHRESULTS) {
        if (activeRefinement === CONTENTTYPEARTICLE || 
            activeRefinement === CONTENT_TYPES.DOCUMENT || 
            activeRefinement === CONTENTTYPEMEDIA || 
            activeRefinement === CONTENTTYPEWEBPAGE) {
          setDisplayMode(DISPLAYMODE.LIST);
        } else if (activeRefinement === CONTENTTYPEPRODUCT) {
          setDisplayMode(ENABLE_HOVER_MODE ? DISPLAYMODE.GRIDCOMPACT : DISPLAYMODE.GRIDEXPANDED);
        }
      }
      
      // For single type searches
      if (isSingleTypeSearch) {
        if (searchContentType === CONTENTTYPEARTICLE || 
            searchContentType === CONTENT_TYPES.DOCUMENT || 
            searchContentType === CONTENTTYPEMEDIA || 
            searchContentType === CONTENTTYPEWEBPAGE) {
          setDisplayMode(DISPLAYMODE.LIST);
        } else if (searchContentType === CONTENTTYPEPRODUCT) {
          setDisplayMode(ENABLE_HOVER_MODE ? DISPLAYMODE.GRIDCOMPACT : DISPLAYMODE.GRIDEXPANDED);
        }
      }
    }
  }, [activeRefinement, page, documentRef, isSingleTypeSearch]);

  //props did update aka linking to another page, even with same component
  //reset all state items
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!isFirstReRender.current) {
      setSortValue(defaultSort);
      searchFilters.setActiveFacets([]);
      setSearchResults(null);
      setAutoCorrectOverride(null);
      setSearchTerm(searchQuery.urlParams.get("q") || "");
      setRenderedSearchResults(null);
      setNumberToShow(NUM_OF_ROWS);
      searchFilters.setMobileFilterMenuOpen(false);
      searchFilters.setMobileSelectedFacetGroup(null);
      searchFilters.setMobileSelectedToggleGroup(null);
      searchFilters.setFilterFacetGroups(null);
      setRefinements({ counts: [], didYouMean: [] });
      setActiveRefinement(null);
      setGotRefinements(false);
      setLoadingResults(true);
      setPageNum(1);
      setReRunSearch(!reRunSearch);
    }
  }, [page]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    try {
      if (isFirstRender.current) {
        isFirstRender.current = false; // toggle flag after first render/mounting
        if (servletUrl || isSingleTypeSearch) {
          // if this is a curated listing or single type search, do not run the refinements
          _getSearchResults();
        } else {
          _getRefinements();
        }
      }
    } catch (e) {
      console.error(e);
    }
  }, []);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    try {
      setActiveRefinement(null);
      searchFilters.setActiveFacets([]);
    } catch (e) {
      console.error(e);
    }
  }, [searchTerm]);

  // Sync URL with search state changes
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!isFirstRender.current) {
      searchQuery.updateSearchURL(
        searchTerm,
        activeRefinement,
        sortValue,
        pageNum,
        searchFilters.activeFacets
      );
    }
  }, [searchTerm, activeRefinement, sortValue, pageNum, searchFilters.activeFacets]);

  // on facet change
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    try {
      //don't want this running twice on initial load
      if (isFirstReRender.current) {
        isFirstReRender.current = false; // toggle flag after first render/mounting
      } else {
        if (!servletUrl) {
          if (isFirstRefinementGetRender)
            if (isFirstRefinementGetRender.current) {
              isFirstRefinementGetRender.current = false; // toggle flag after first render/mounting of isFirstRefinementGetRender
              _getSearchResults();
            } else {
              _getSearchResults(() => _getRefinements(false));
            }
        } else {
          _getSearchResults();
        }
      }
    } catch (e) {
      console.error(e);
    }
  }, [reRunSearch]);

  // Use hook methods wrapped with local state dependencies
  const _setupActiveFacets = useCallback((facetGroups, updateActiveFacets = true) => {
    searchFilters.setupActiveFacets(facetGroups, updateActiveFacets);
  }, [searchFilters]);

  const _removeFacet = useCallback((facet) => {
    searchFilters.removeFacet(facet, isMobileNav, setLoadingResults, setPageNum, setReRunSearch);
  }, [searchFilters, isMobileNav]);

  const _addFacet = useCallback((facet) => {
    searchFilters.addFacet(facet, setLoadingResults, setPageNum, setReRunSearch);
  }, [searchFilters]);

  const _removeAllFacet = useCallback((removeUrlFilters = true) => {
    searchFilters.removeAllFacet(
      removeUrlFilters,
      searchQuery.removeFilterFromPageURL,
      isMobileNav,
      setLoadingResults,
      setPageNum,
      setReRunSearch
    );
  }, [searchFilters, searchQuery, isMobileNav]);

  const _removeUrlFilter = () => {
    searchQuery.removeUrlFilter(setLoadingResults, setPageNum, setReRunSearch, reRunSearch);
  };

  const _getRefinementsQueryParam = () => {
    return searchQuery.getRefinementsQueryParam(activeRefinement);
  };

  const _buildFiltersQueryString = () => {
    return searchQuery.buildFiltersQueryString(searchFilters.activeFacets, searchTerm);
  };

  let refinementServlet = searchQuery.buildRefinementServlet(
    patentSearchServletURL,
    brsmSearchURL,
    configurationPath,
    searchTerm,
    searchFilters.activeFacets
  );

  const _getRefinements = (reRun = true) => {
    if (isSingleTypeSearch) return;
    fetchRefinementsCached(refinementServlet)
      .then((res) => {
        if (res.ok) {
          return res.json();
        }
        throw res;
      })
      .then((data) => {
        if (data) {
          const { counts = [], didYouMean = [] } = data;
          if (data.autoCorrectQuery) {
            setSearchTerm(data.autoCorrectQuery);
            setAutoCorrectOverride(data.autoCorrectQuery);
          }

          // Remove mediaCollection refinement, not needed for Site Search
          const countsArray = counts.filter((item) => item.id !== CONTENTTYPEMEDIA);
          setRefinements({ counts: countsArray, didYouMean: didYouMean });

          // If we get refinements, set to true
          if (countsArray.length > 0) {
            setGotRefinements(true);
            // determine whether or not search results need to be ran again, if not
            // add refinement and clear facets without triggering rerun
            //find first refinement with items in it
            let firstFound = countsArray.find((element) => element.count > 0);
            //if none use first in array
            if (!firstFound) {
              firstFound = countsArray[0];
            }
            if (reRun) {
              _addRefinement(firstFound["id"]);
            } else {
              if (activeRefinement === null) {
                setActiveRefinement(firstFound["id"]);
                setReRunSearch(!reRunSearch);
              }
            }
          }
        }
      })
      .catch((error) => {
        setRefinements({ counts: [], didYouMean: [] });
        console.error(error);
      });
  };

  // Adding Refinement
  const _addRefinement = (refinement) => {
    setActiveRefinement(refinement);
    setPageNum(1);
    setNumberToShow(NUM_OF_ROWS);
    setReRunSearch(!reRunSearch);
  };

  let completeServletURL =
    servletUrl + "?componentConfigPath=" + configurationPath + _buildFiltersQueryString();

  //get Search results
  const _getSearchResults = (callback) => {
    setLoadingResults(true);
    const contentDocument = page.getContent(documentRef);
    if (!contentDocument) return null;
    const { contentType = null } = contentDocument.getData();
    // reset rendered results if it's page 1 and there are already rendered results
    if (pageNum === 1 && searchResults !== null) {
      setRenderedSearchResults(null);
    }
    let sortServletURL = completeServletURL;
    if (contentType !== CURATEDLISTING) {
      sortServletURL = sortServletURL + `&page=${pageNum}`;
      if (sortValue?.toLowerCase() === SORT_OPTION.PUBLISHEDDATE) {
        sortServletURL = sortServletURL + `&sort=${SORTMOSTRECENT}`;
      } else if (sortValue?.toLowerCase() === SORT_OPTION.ALPHABETICAL) {
        sortServletURL = sortServletURL + `&sort=${SORTALPHABETICAL}`;
      }
    }

    fetch(sortServletURL)
      .then((response) => {
        if (response.ok) {
          return response.json();
        }
        throw response;
      })
      .then((data) => {
        setSearchResults(data);
        if (pageNum !== 1) {
          setRenderedSearchResults([...renderedSearchResults, ...data.results]);
        } else {
          if(data.results){
            setRenderedSearchResults([...data.results]);
          }
        }
        if (data && data.facetgroups) {
          // Always update facet name map, but only set active facets on first render
          _setupActiveFacets(data.facetgroups, isFirstRender.current);
          searchFilters.buildFinalFacetGroups(data.facetgroups);
        }
        const filterQueryParam = searchQuery.urlParams.get("filters")
        if (filterQueryParam && filterQueryParam.startsWith(CATEGORY_FILTER)) {
          setCategoryFilter(filterQueryParam.substring(CATEGORY_FILTER.length).replaceAll("-"," "))
        }
        else {
          setCategoryFilter("")
        }
      })
      .catch((error) => {
        console.error(error);
      })
      .finally(() => {
        setLoadingResults(false);
        if (callback) {
          callback();
        }
      });
  };

  const _handleSortChange = (e) => {
    setPageNum(1);
    setNumberToShow(NUM_OF_ROWS);
    var newSortValue = e.target.value;
    setSortValue(newSortValue);
    setReRunSearch(!reRunSearch);
  };
  const _handleLoadMore = (contentType) => {
    setPageNum(pageNum + 1);
    if (contentType !== CURATEDLISTING) {
      setReRunSearch(!reRunSearch);
    }
    setNumberToShow(numberToShow + NUMBER_TO_LOAD_MORE);
  };
  const _handleSearchTermChange = (value) => {
    setSearchTerm(value);
    setPageNum(1);
    setReRunSearch(!reRunSearch);
  };
  const _handleDisplayModeChange = (e) => {
    setDisplayMode(e);
  };

  try {
    const contentDocument = page.getContent(documentRef);
    if (!contentDocument) return null;

    const {
      type = null,
      contentType = null,
      resultsForLabel = null,
      showResultsForLabel = null,
      articlesRefinementLabel = null,
      productsRefinementLabel = null,
      webpagesRefinementLabel = null,
      documentRefinementLabel = null,
      foodserviceRefinementLabel = null,
      retailRefinementLabel = null,
      categoryNoResultsTitle = null,
      categoryNoResultsNeedHelpDescription = {},
    } = contentDocument.getData();
    if (!contentType) return null;

    const searchContentType = getSearchContentType(contentType);

    const showSearchBar = contentType === SITESEARCHRESULTS || contentType === PATENTSEARCHLISTING;
    const showResultsFilter =
      contentType !== SITESEARCHRESULTS &&
      contentType !== CURATEDLISTING &&
      contentType !== PATENTSEARCHLISTING;
    const showRefinements =
      contentType === SITESEARCHRESULTS || contentType === PATENTSEARCHLISTING;
    const showFilter =
      contentType !== PATENTSEARCHLISTING &&
      activeRefinement !== CONTENTTYPEWEBPAGE &&
      searchFilters.filterFacetGroups?.length > 0;
    const hideSortSelect = contentType === PATENTSEARCHLISTING ? true : hideSort;
    const hideDisplayMode = contentType === PATENTSEARCHLISTING  || (contentType === SITESEARCHRESULTS && activeRefinement !== CONTENTTYPEPRODUCT) || (isSingleTypeSearch && searchContentType !== CONTENTTYPEPRODUCT);

    //Set default display mode based on contentType
    var displayModeDefault = ENABLE_HOVER_MODE ? DISPLAYMODE.GRIDCOMPACT : DISPLAYMODE.GRIDEXPANDED;
    if (contentType === SITESEARCHRESULTS) {
      if (activeRefinement === CONTENTTYPEPRODUCT) {
        displayModeDefault = ENABLE_HOVER_MODE ? DISPLAYMODE.GRIDCOMPACT : DISPLAYMODE.GRIDEXPANDED;
      } else {
        displayModeDefault = DISPLAYMODE.LIST;
      }
    } else if (contentType === PATENTSEARCHLISTING) {
      displayModeDefault = DISPLAYMODE.LIST;
    } else if (contentType === CURATEDLISTING) {
      displayModeDefault = ENABLE_HOVER_MODE ? DISPLAYMODE.GRIDCOMPACT : DISPLAYMODE.GRIDEXPANDED;
    } else if (isSingleTypeSearch) {
      if (searchContentType === CONTENTTYPEPRODUCT) {
        displayModeDefault = ENABLE_HOVER_MODE ? DISPLAYMODE.GRIDCOMPACT : DISPLAYMODE.GRIDEXPANDED;
      } else {
        displayModeDefault = DISPLAYMODE.LIST;
      }
    } else {
      displayModeDefault = ENABLE_HOVER_MODE ? DISPLAYMODE.GRIDCOMPACT : DISPLAYMODE.GRIDEXPANDED;
    }

    // If contentType = SiteSearchResults
    if (contentType === SITESEARCHRESULTS) {
      completeServletURL =
        brsmSearchURL +
        "?contentType=" +
        _getRefinementsQueryParam() +
        "&componentConfigPath=" +
        configurationPath +
        "&rows=" +
        NUM_OF_ROWS +
        _buildFiltersQueryString();
      if (searchTerm)
        completeServletURL = completeServletURL + "&q=" + encodeURIComponent(searchTerm);
    }

    //set servlet URL for Patent Search Listing
    if (contentType === PATENTSEARCHLISTING) {
      const query = encodeURIComponent(searchTerm);
      completeServletURL =
        patentSearchServletURL +
        "?contentType=" +
        _getRefinementsQueryParam() +
        "&componentConfigPath=" +
        configurationPath +
        "&q=" +
        query +
        "&rows=" +
        NUM_OF_ROWS;
    }

    // If contentType = SingleTypeListing
    if (
      contentType !== SITESEARCHRESULTS &&
      contentType !== CURATEDLISTING &&
      contentType !== PATENTSEARCHLISTING
    ) {
      const singleTypeContentType = encodeURIComponent(searchContentType);
      completeServletURL =
        brsmSearchURL +
        "?contentType=" +
        singleTypeContentType +
        "&componentConfigPath=" +
        configurationPath +
        "&rows=" +
        NUM_OF_ROWS +
        _buildFiltersQueryString();
      if (searchTerm) {
        completeServletURL = completeServletURL + "&q=" + encodeURIComponent(searchTerm);
      }
    }

    //sorts if needed, and chooses the right type of result card as well
    const { counts = [], didYouMean = [] } = refinements;
    
    const searchTotalCount = totalSearchItemsCount(counts, gotRefinements);

    const searchResultsExist = searchResults && renderedSearchResults && renderedSearchResults.length > 0;
    const searchResultsCount = (searchResults && searchResults.total) || (renderedSearchResults && renderedSearchResults.length) || 0;

    const { sortOptionAlphabetical, sortOptionFeatured, sortOptionPublishedDate } =
      window.globalProperties || {};
    const sortOptions = [
      {
        value: SORT_OPTION.ALPHABETICAL,
        display: sortOptionAlphabetical,
      },
      {
        value: SORT_OPTION.FEATURED,
        display: sortOptionFeatured,
      },
    ];

    if (activeRefinement === CONTENTTYPEARTICLE || searchContentType === CONTENTTYPEARTICLE) {
      sortOptions.push({
        value: SORT_OPTION.PUBLISHEDDATE,
        display: sortOptionPublishedDate,
      });
    }

    const getRefinementLabelIconInfo = () => {
      return [
        {
          id: REFINEMENTS.PRODUCT.id,
          label: productsRefinementLabel,
          icon: REFINEMENTS.PRODUCT.icon,
        },
        {
          id: REFINEMENTS.ARTICLE.id,
          label: articlesRefinementLabel,
          icon: REFINEMENTS.ARTICLE.icon,
        },
        {
          id: REFINEMENTS.DOCUMENT.id,
          label: documentRefinementLabel,
          icon: REFINEMENTS.DOCUMENT.icon,
        },
        {
          id: REFINEMENTS.WEBPAGE.id,
          label: webpagesRefinementLabel,
          icon: REFINEMENTS.WEBPAGE.icon,
        },
        {
          id: REFINEMENTS.FOODSERVICE.id,
          label: foodserviceRefinementLabel,
          icon: REFINEMENTS.FOODSERVICE.icon,
        },
        {
          id: REFINEMENTS.RETAIL.id,
          label: retailRefinementLabel,
          icon: REFINEMENTS.RETAIL.icon,
        },
      ];
    };

    function _moveFieldsets() {
      const fieldsets = document.querySelectorAll('.desktop-filter__group');
      const container = fieldsets && fieldsets[0] && fieldsets[0].parentNode;
      
      if (!container) return;
      
      const checkedFieldsets = [];
      const uncheckedFieldsets = [];
  
      fieldsets.forEach(fieldset => {
        const checkboxes = fieldset.querySelectorAll('input[type="checkbox"]');
        const isChecked = Array.from(checkboxes).some(checkbox => checkbox.checked);
  
        if (isChecked) {
          checkedFieldsets.push(fieldset);
        } else {
          uncheckedFieldsets.push(fieldset);
        }
      });
  
      // Move checked fieldsets to the top
      checkedFieldsets.forEach(fieldset => container.appendChild(fieldset));
  
      // Move unchecked fieldsets back to their original order
      uncheckedFieldsets.sort((a, b) => a.dataset.order - b.dataset.order)
                        .forEach(fieldset => container.appendChild(fieldset));
    }

    const toolbarContextValue = {
      results: {
        count:
          (contentType === SITESEARCHRESULTS || contentType === PATENTSEARCHLISTING) &&
          gotRefinements === true
            ? searchTotalCount
            : searchResultsCount,
        label: type,
        searchTerm: searchTerm,
        resultsForLabel: resultsForLabel,
        loadingResults: loadingResults,
        categoryFilter: categoryFilter,
      },
      pageType: contentType,
      sort: {
        hide: hideSortSelect,
        currentValue: sortValue?.toLowerCase(),
        handleChange: _handleSortChange,
        options: sortOptions,
      },
      showUrlFilters: (contentType === CURATEDLISTING || isSingleTypeSearch) ? true : false,
      clearURLFiltersOnRemoveAllFacets: (contentType === CURATEDLISTING || isSingleTypeSearch ) ? true : false,
      urlFilters: {
        filters: searchQuery.getFilterObjectsFromQueryParam(),
        removeFilter: _removeUrlFilter,
        removeAllFilters: _removeAllFacet
      },
      filters: {
        facets: searchFilters.activeFacets,
        removeFacet: _removeFacet,
        removeAllFacets: _removeAllFacet,
      },
      setFilterMenuOpen: searchFilters.setMobileFilterMenuOpen,
      showFilterBy: showFilter,
      displaymode: {
        option1: ENABLE_HOVER_MODE ? { value: DISPLAYMODE.GRIDCOMPACT, label: window.globalProperties["displayModeGridCompactLabel"] ? window.globalProperties["displayModeGridCompactLabel"]: "Compact View", className: "icon-simple-view" } : null,
        option2: { value: DISPLAYMODE.GRIDEXPANDED, label: window.globalProperties["displayModeGridExpandedLabel"] ? window.globalProperties["displayModeGridExpandedLabel"]: "Expanded View", className: "icon-details-view" },
        option3: { value: DISPLAYMODE.LIST, label: window.globalProperties["displayModeListViewLabel"] ? window.globalProperties["displayModeListViewLabel"]: "List View", className: "icon-list-view" },
        defaultSelected: displayModeDefault,
        name: "displayModeToggleSearch",
        label: window.globalProperties["displayModeToggleLabel"] ? window.globalProperties["displayModeToggleLabel"]: "Toggle View",
        ariaLabel: window.globalProperties["displayModeToggleAriaLabel"] ? window.globalProperties["displayModeToggleAriaLabel"]: "Toggle View",
        onChange: _handleDisplayModeChange,
        hidden: isMobile || hideDisplayMode,
      }
    };
    const searchInputContextValue = {
      search: _handleSearchTermChange,
    };

    const searchRefinementsContextValue = {
      refinements: {
        addRefinement: _addRefinement,
        getRefinements: _getRefinements,
        options: refinements.counts,
        count: gotRefinements === true ? searchTotalCount : 0,
        activeRefinement: activeRefinement,
        setActiveRefinement: setActiveRefinement,
      },
      searchTerm: searchTerm,
      labels: {
        showResultsForLabel,
        resultsForLabel,
      },
      labelIconInfo: getRefinementLabelIconInfo(),
    };

    const TITLE_FILTER_BY = window["globalProperties"]?.["filterByLabel"] + ":";
    const filterContextValue = {
      filterTitle: TITLE_FILTER_BY,
      defaultToggleValues: defaultDesktopToggleValues(),
      facetGroups: {
        groups: searchFilters.filterFacetGroups,
        selectedFacetGroup: searchFilters.mobileSelectedFacetGroup,
        selectedToggleGroup: searchFilters.mobileSelectedToggleGroup,
        updateSelectedFacetGroup: searchFilters.setMobileSelectedFacetGroup,
        updateSelectedToggleGroup: searchFilters.setMobileSelectedToggleGroup,
      },
      facets: {
        activeFacets: searchFilters.activeFacets,
        removeFacet: _removeFacet,
        addFacet: _addFacet,
      },
      filterMenuOpen: searchFilters.mobileFilterMenuOpen,
      setFilterMenuOpen: searchFilters.setMobileFilterMenuOpen,
      closeFilters: searchFilters.closeFilters,
      moveFieldsets: _moveFieldsets,
    };

    // Determine if we should show no results (but keep the layout intact for site search with refinements)
    const showNoResultsInLayout = 
      !loadingResults &&
      (!renderedSearchResults || renderedSearchResults.length === 0) &&
      (searchTotalCount === null || searchTotalCount <= 0 || searchTerm === undefined || searchTerm === "");

    // Show no results page WITHOUT layout only when there are no filters and it's initial search
    if (
      contentType === SITESEARCHRESULTS &&
      showNoResultsInLayout &&
      !searchQuery.urlParams.get("filters") &&
      !gotRefinements
    ) {
      return (
        <>
          <SearchInput
            setAutoCorrectOverride={setAutoCorrectOverride}
            autoCorrectOverride={autoCorrectOverride}
            setSearchTerm={setSearchTerm}
            setReRunSearch={setReRunSearch}
            reRunSearch={reRunSearch}
          />
          <NoResults
            noResultsPopularProductCodes={noResultsPopularProductCodes}
            productServletURL={productServletURL}
            searchValue={searchTerm}
            suggestions={didYouMean}
          />
        </>
      );
    }


    // Show left rail content only when results are ready
    const shouldShowLeftRailContent = !loadingResults && searchResultsExist;

    return (
      <div className={`${page.isPreview() ? "has-edit-button" : ""} search`}>
        {loadingResults && (
          <div className="search__loading-overlay">
            <div className="search__loading-overlay-background"></div>
            <LoadingSpinner
              showAlert={true}
              alertMessage={(searchTerm || activeRefinement)
                ? `Loading results for ${searchTerm || activeRefinement}`
                : `Loading results`}>
            </LoadingSpinner>
          </div>
        )}
        <BrManageContentButton content={contentDocument} />

        {/* Search Input */}
        {showSearchBar && (
          <SearchInput
            setAutoCorrectOverride={setAutoCorrectOverride}
            autoCorrectOverride={autoCorrectOverride}
            setSearchTerm={setSearchTerm}
            setReRunSearch={setReRunSearch}
            reRunSearch={reRunSearch}
          />
        )}
        <div className="search__inner" id="searchTop">
          <SearchProviders
            toolbarContext={toolbarContextValue}
            filterContext={filterContextValue}
            inputFilterContext={searchInputContextValue}
            refinementContext={searchRefinementsContextValue}
          >
            <div className="search__left-right-wrapper">
              <div className="search__left-rail">
                <div className="search__top-bar">
                {showRefinements && shouldShowLeftRailContent && (
                  <Mobile>
                    <SearchRefinementsController />
                  </Mobile>
                )}

                {shouldShowLeftRailContent && <SearchToolbarInfo />}
              </div>
                {showRefinements && shouldShowLeftRailContent && (
                  <Desktop>
                    <SearchRefinementsController />
                  </Desktop>
                )}

                {showResultsFilter && shouldShowLeftRailContent && <SearchInputFilter />}

                {showFilter && shouldShowLeftRailContent && <Filter />}
              </div>
              <div className="search__results-wrapper" role="region" aria-label="Search results">

                <div id="modal-container" className="modal-container" ref={modalContainerRef}></div>

                {searchResultsExist && searchResultsCount > 0 && (
                  <div>
                    <SearchToolbar/>
                    <ul
                      className={`search__results-list ${displayMode}`}
                      id="searchResultsList"
                      role={contentType === PATENTSEARCHLISTING ? "tabpanel" : ""}
                      aria-labelledby={contentType === PATENTSEARCHLISTING ? activeRefinement : ""}
                      aria-label={contentType !== PATENTSEARCHLISTING ? `Search results for ${searchTerm || activeRefinement || 'all items'}` : undefined}>

                      <SearchResultRenderer
                        renderedSearchResults={renderedSearchResults}
                        contentType={contentType}
                        sortValue={sortValue}
                        numberToShow={numberToShow}
                        displayMode={displayMode}
                        modalContainerRef={modalContainerRef}
                        positionModal={positionModal}
                        productHeight={productHeight}
                        productWidth={productWidth}
                        isSingleTypeSearch={isSingleTypeSearch}
                      />
                    </ul>
                  </div>
                )}
                {searchResultsExist && searchResultsCount > numberToShow && (
                  <div className="search__load-more-wrapper">
                    <button
                      onClick={() => {
                        _handleLoadMore(contentType);
                      }}
                      className="search__load-more btn btn--secondary"
                      tabIndex={0}
                    >
                      {window.globalProperties["loadMoreLabel"]
                        ? window.globalProperties["loadMoreLabel"]
                        : "Load More"}
                    </button>
                  </div>
                )}
                {searchResultsExist && searchResultsCount === 0 && (
                  <div className="no-results__aid"> 
                    {categoryNoResultsTitle && (
                      <h2 className="no-results__aid__guide__title">{categoryNoResultsTitle}</h2>
                    )}
                    {categoryNoResultsNeedHelpDescription && categoryNoResultsNeedHelpDescription.value && (
                      <div className="no-results__aid__suggestions"
                        dangerouslySetInnerHTML={{
                        __html: categoryNoResultsNeedHelpDescription.value,
                        }}
                      />
                    )}
                  </div>
                )}
                {showNoResultsInLayout && gotRefinements && !loadingResults && (
                  <div role="status" aria-live="polite">
                    <NoResults
                      noResultsPopularProductCodes={noResultsPopularProductCodes}
                      productServletURL={productServletURL}
                      searchValue={searchTerm}
                      suggestions={didYouMean}
                    />
                  </div>
                )}
              </div>
            </div>
          </SearchProviders>
        </div>
      </div>
    );
  } catch (e) {
    console.error("SearchController error", e);
    return <ErrorComponent error={e} componentName={"SearchController"} />;
  }
}

export default SearchController;